// src/components/subscription/PremiumFeatureBadge.tsx
import React from 'react';
import { Crown, Lock } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';

interface PremiumFeatureBadgeProps {
  feature: 'medium' | 'premium';
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Component to indicate a premium feature and optionally lock it
 * if the user doesn't have the required subscription
 */
const PremiumFeatureBadge: React.FC<PremiumFeatureBadgeProps> = ({
  feature,
  onClick,
  children,
  className = ''
}) => {
  const { subscription, setShowUpgradeModal } = useSubscription();
  
  // Determine if the feature is accessible based on the subscription
  const isAccessible = () => {
    if (!subscription) return false;
    
    if (feature === 'medium') {
      return subscription.planType === 'medium' || subscription.planType === 'premium';
    } else {
      return subscription.planType === 'premium';
    }
  };
  
  const handleClick = () => {
    if (!isAccessible()) {
      // Show upgrade modal
      setShowUpgradeModal(true);
      
      // Call onClick if provided
      if (onClick) {
        onClick();
      }
    }
  };
  
  // If accessible, just return the children
  if (isAccessible()) {
    return <>{children}</>;
  }
  
  // If not accessible, show a badge and possibly lock content
  return (
    <div 
      className={`relative ${className}`}
      onClick={handleClick}
    >
      {/* Overlay for content if children are provided */}
      {children && (
        <div className="absolute inset-0 bg-gray-100 bg-opacity-70 flex items-center justify-center rounded-lg z-10">
          <div className="bg-white p-2 rounded-lg shadow-md flex items-center gap-2">
            <Lock size={14} className="text-gray-600" />
            <span className="text-sm font-medium">
              {feature === 'premium' ? 'Premium' : 'Medium'} Feature
            </span>
          </div>
        </div>
      )}
      
      {/* Badge */}
      <div className={`${!children ? 'inline-flex' : 'absolute top-1 right-1 z-20'} items-center gap-1 px-2 py-1 bg-${feature === 'premium' ? 'purple' : 'blue'}-100 text-${feature === 'premium' ? 'purple' : 'blue'}-700 rounded-full border border-${feature === 'premium' ? 'purple' : 'blue'}-200 text-xs font-medium`}>
        <Crown size={12} />
        <span>{feature === 'premium' ? 'Premium' : 'Medium'}</span>
      </div>
      
      {/* Children with reduced opacity if not accessible */}
      {children && <div className="opacity-20">{children}</div>}
    </div>
  );
};

export default PremiumFeatureBadge;